import {useEffect,useRef,useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Button} from '@secretaa/ui';
import type {ProjectQueryRequest,ProjectQuerySourceDescriptor} from '@secretaa/contracts-data';
import {api} from '../../app/api.ts';
import {FormError} from '../../shared/form.tsx';
import {queryError} from './query-errors.ts';

/** Parent keys this command by tenant, permission epoch and cursor-free query. Retries reuse its receipt key. */
export function QuerySourceSave({workspaceId,projectId,query,enabled}:{workspaceId:string;projectId:string;query:ProjectQueryRequest;enabled:boolean}){
 const {t,i18n}=useTranslation(),[pending,setPending]=useState(false),[saved,setSaved]=useState<ProjectQuerySourceDescriptor|null>(null),[error,setError]=useState<unknown>(null);
 const command=useRef<{sourceId:string;idempotencyKey:string}|null>(null),flight=useRef<AbortController|null>(null);
 useEffect(()=>()=>{flight.current?.abort();},[]);
 async function save(){
  if(!enabled||flight.current||saved)return;
  command.current??={sourceId:crypto.randomUUID(),idempotencyKey:crypto.randomUUID()};
  const controller=new AbortController();flight.current=controller;setPending(true);setError(null);
  try{const value=await api.querySources.save(workspaceId,projectId,{...command.current,expectedBaseVersion:0,query:{...query,page:{limit:query.page.limit}}},controller.signal);if(!controller.signal.aborted)setSaved(value);}
  catch(e){if(!controller.signal.aborted)setError(e);}
  finally{if(flight.current===controller)flight.current=null;if(!controller.signal.aborted)setPending(false);}
 }
 // Stale results or denied reads must not expose a previous command receipt.
 if(!enabled)return null;
 return <section className="space-y-2 rounded-lg border p-3" aria-label={t('projectQuery.sourceSave.title')}>
  <h3 className="font-medium">{t('projectQuery.sourceSave.title')}</h3>
  <p className="text-sm text-(--saa-fg-muted)">{t('projectQuery.sourceSave.hint')}</p>
  {saved?<p role="status">{t('projectQuery.sourceSave.saved',{version:new Intl.NumberFormat(i18n.language).format(saved.sourceRef.version)})}</p>:<Button variant="secondary" disabled={pending} onClick={()=>void save()}>{t(pending?'projectQuery.sourceSave.saving':'projectQuery.sourceSave.save')}</Button>}
  {saved?<details><summary>{t('projectQuery.sourceSave.reference')}</summary><p className="break-all text-sm">{saved.sourceRef.id}</p></details>:null}
  {error?<FormError message={queryError(t,error)}/>:null}
 </section>;
}
